import React, { Suspense, useState } from 'react';
import {
  notification,
  Tag,
  type TablePaginationConfig,
  type TableProps,
} from 'antd';
import { ICndn, ISearchCndnReq, ISearchCndnRes } from '@/interfaces/cndn';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { twMerge } from 'tailwind-merge';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  getCndnColor,
  getCndnReasonName,
  getCndnTypeName,
} from '@/helpers/cndnHelper';
import { DeleteFilled } from '@ant-design/icons';
import { deleteCndn } from '@/adapters/cndn';

interface IProps {
  loading: boolean;
  criteria: ISearchCndnReq;
  cndn: ISearchCndnRes;
  onDelete: (id: number) => void;
  onEditData: (item: ICndn) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ICndn> | SorterResult<ICndn>[],
  ) => void;
}

const _cndnModal: IDataModal<ICndn> = {
  open: false,
};

const CndnTable = ({
  loading,
  criteria,
  cndn,
  onDelete,
  onTableChange,
}: IProps) => {
  dayjs.extend(relativeTime);
  const [deleteModal, setDeleteModal] = useState<IDataModal<ICndn>>(_cndnModal);

  const columns: TableProps<ICndn>['columns'] = [
    {
      title: 'รหัส',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold text-blue-700',
      width: 130,
      align: 'center',
      render: (_, rec) => (
        <div className="flex flex-col ">
          <div
            onClick={() => window.open(`/cndn-note/${rec.id}`, '_blank')}
            className={twMerge(
              'text-blue-700 underline cursor-pointer underline-offset-2',
              !rec?.isActive && 'text-red-500',
            )}
          >
            {_}
          </div>
        </div>
      ),
    },
    {
      title: 'BU',
      dataIndex: 'bu',
      key: 'bu',
      className: 'font-medium',
      align: 'center',
      render: (_, rec) => rec?.order?.businessUnitName,
      width: 60,
    },
    {
      title: 'ประเภท',
      dataIndex: 'type',
      key: 'type',
      className: 'font-medium',
      align: 'center',
      width: 100,
      fixed: 'right',
      render: _ => {
        const name = getCndnTypeName(_);
        const status = getCndnColor(_);
        return (
          <div
            className={twMerge(
              'truncate',
              status?.bg,
              status?.text,
              'rounded py-0.5',
            )}
          >
            {name}
          </div>
        );
      },
    },
    {
      title: 'สาเหตุ',
      dataIndex: 'reason',
      key: 'reason',
      className: 'font-medium',
      align: 'center',
      width: 100,
      fixed: 'right',
      render: _ => getCndnReasonName(_),
    },
    {
      title: 'วันที่',
      dataIndex: 'docDate',
      key: 'docDate',
      width: 100,
      align: 'center',
      className: 'font-medium text-black',
      render: _ => formatDate(_),
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-bold',
      render: (_, rec) => (
        <div className="truncate">{`${rec?.order?.customerCode} - ${rec?.order?.customerName}`}</div>
      ),
    },
    {
      title: 'Order No.',
      dataIndex: 'soNo',
      key: 'soNo',
      align: 'center',
      width: 120,
      render: (_, rec) => (
        <div className="truncate">{`${rec?.order?.soNo || ''}`}</div>
      ),
    },
    {
      title: 'IV No.',
      dataIndex: 'ivNo',
      key: 'ivNo',
      align: 'center',
      width: 120,
      render: (_, rec) => (
        <div className="truncate">{`${rec?.order?.ivNo || ''}`}</div>
      ),
    },
    {
      title: 'PO No.',
      dataIndex: 'poNo',
      key: 'poNo',
      align: 'center',
      width: 120,
      render: (_, rec) => (
        <div className="truncate">{`${rec?.order?.poNo || ''}`}</div>
      ),
    },
    {
      title: 'มูลค่าที่แจ้งหนี้',
      dataIndex: 'invoiceAmount',
      key: 'invoiceAmount',
      align: 'right',
      width: 110,
      className: 'font-bold',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'มูลค่าที่ถูกต้อง',
      dataIndex: 'newInvoiceAmount',
      key: 'newInvoiceAmount',
      align: 'right',
      width: 110,
      className: 'font-bold text-green-700',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'ส่วนต่าง',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      className: 'font-bold',
      align: 'right',
      width: 100,
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'ส่วนลด',
      dataIndex: 'discountAmount',
      key: 'discountAmount',
      className: 'font-bold',
      align: 'right',
      width: 90,
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'VAT',
      dataIndex: 'vatAmount',
      key: 'vatAmount',
      align: 'right',
      width: 90,
      className: 'font-bold',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'ยอดสุทธิ',
      dataIndex: 'grandTotalAmount',
      key: 'grandTotalAmount',
      align: 'right',
      width: 110,
      className: 'font-bold text-blue-700',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'สถานะ',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      align: 'center',
      render: (_: boolean) => {
        return (
          <Tag color={_ ? `green` : 'red'} className="min-w-[60px] text-center">
            {_ ? 'สำเร็จ' : 'ยกเลิก'}
          </Tag>
        );
      },
    },
    {
      dataIndex: 'isActive',
      key: 'isActive',
      width: 40,
      render: (_, rec) => {
        return (
          <div className="flex flex-row divide-x divide-dashed items-center w-full justify-center">
            {_ && (
              <DeleteFilled
                className="text-red-700 px-1.5"
                onClick={() =>
                  setDeleteModal({
                    open: true,
                    item: rec,
                    id: rec.id,
                  })
                }
              />
            )}
          </div>
        );
      },
    },
  ];

  const onDeleteCndn = async () => {
    try {
      await deleteCndn(Number(deleteModal?.id));
      notification.success({
        message: 'ยกเลิกใบเพิ่มหนี้/ลดหนี้สำเร็จ',
      });
      onDelete(Number(deleteModal?.id || 0));
      setDeleteModal({ ..._cndnModal });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<ICndn>
        columns={columns}
        criteria={criteria}
        data={cndn}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1800}
        scroll
      />
      <Suspense>
        <DeleteConfirmModal
          content={`คุณต้องการลบ '${deleteModal?.item?.code}' ใช่หรือไม่`}
          handleCancel={() => {
            setDeleteModal({ ..._cndnModal });
          }}
          handleOk={() => {
            onDeleteCndn();
          }}
          open={deleteModal?.open}
          title={`ใบเพิ่มหนี้/ลดหนี้ '${deleteModal?.item?.code}'`}
        />
      </Suspense>
    </div>
  );
};

export default CndnTable;
